import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, uploadFile } from '../api';
import { parsePln } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { errorText, PURCHASES_KEY, type Purchase } from './types';
import { useUnsavedGuard, validForm } from '../ui/unsaved';

/** "Dodaj fakturę": a purchase invoice from outside KSeF (e.g. a foreign one), with its file (PDF or photo). */
export function ManualPurchaseForm({ onDone, onCancel }: { onDone: (p: Purchase) => void; onCancel: () => void }) {
  const queryClient = useQueryClient();
  const [d, setD] = useState({ sellerName: '', sellerNip: '', invoiceNumber: '', issueDate: '', dueDate: '', net: '', vat: '' });
  const [file, setFile] = useState<File | null>(null);
  const [local, setLocal] = useState('');
  const save = useMutation({
    mutationFn: async () => {
      const fileId = file ? await uploadFile(file) : null;
      return api<Purchase>('/api/purchases', {
        method: 'POST',
        body: { sellerName: d.sellerName, sellerNip: d.sellerNip, invoiceNumber: d.invoiceNumber, issueDate: d.issueDate, dueDate: d.dueDate || null, netGrosze: parsePln(d.net), vatGrosze: parsePln(d.vat || '0'), fileId },
      });
    },
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: PURCHASES_KEY });
      onDone(p);
    },
  });
  function ready(): boolean {
    try {
      parsePln(d.net);
      parsePln(d.vat || '0');
    } catch (err) {
      return (setLocal((err as Error).message), false);
    }
    setLocal('');
    return true;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (ready()) save.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: Object.values(d).some((v) => v !== '') || file !== null,
    save: async (el) => {
      if (!ready() || !validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });
  const message = local || (save.error ? errorText(save.error, 'Nie udało się zapisać faktury') : '');
  const text = (key: keyof typeof d, label: string, extra: Record<string, string> = {}) => (
    <label className="field">
      <span>{label}</span>
      <input value={d[key]} onChange={(e) => setD({ ...d, [key]: e.target.value })} autoComplete="off" {...extra} />
    </label>
  );
  return (
    <form ref={guardRef} className="fin-add purchase-form" aria-label="Nowa faktura zakupowa" onSubmit={submit} data-feature-id="purchases.list">
      {text('sellerName', 'Sprzedawca')}
      {text('sellerNip', 'NIP (opcjonalnie)')}
      {text('invoiceNumber', 'Numer faktury')}
      {text('issueDate', 'Data wystawienia', { type: 'date' })}
      {text('dueDate', 'Termin płatności (opcjonalnie)', { type: 'date' })}
      <label className="field">
        <span>Netto (zł)</span>
        <NumberInput kind="money" value={d.net} onValueChange={(net) => setD({ ...d, net })} />
      </label>
      <label className="field">
        <span>VAT (zł)</span>
        <NumberInput kind="money" value={d.vat} onValueChange={(vat) => setD({ ...d, vat })} />
      </label>
      <label className="field">
        <span>Plik faktury</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </label>
      <button className="btn primary" type="submit" disabled={save.isPending}>
        Zapisz fakturę
      </button>
      <button className="btn ghost" type="button" onClick={onCancel}>
        Anuluj
      </button>
      {message && (
        <p className="alert" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}
